import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { Amplify } from "aws-amplify";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { amplifyConfig } from "../../configs/cognito";
import { ApiContext } from "../app/context";
import { useWhoAmIQuery } from "../../services/api/__generated__/backend_gateway-types";
import { AuthenticationService as BgwAuthenticationService } from "../../services/api/authentication";
import { AuthenticationService as PAuthenticationService } from "../../services/perspective_api/authentication";
import { AuthenticationContainer } from "../../components/authentication/container";
import { AuthenticationLoading } from "../../components/authentication/loading";

// Configure Amplify
console.log("Configuring Amplify with config:", amplifyConfig);
Amplify.configure(amplifyConfig);

export type IdTokenProviderType = {
  idToken: string | undefined;
};

export const IdTokenContext = createContext<IdTokenProviderType>({
  idToken: undefined,
});

const { Provider: IdTokenProvider } = IdTokenContext;

const Unauthorized = () => {
  const handleAuthenticationSuccess = () => {
    // The authentication context will handle the state change
    // and re-render the component tree
    // Use a longer delay to ensure the auth state is updated, especially for challenge flows
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <AuthenticationContainer
      onAuthenticationSuccess={handleAuthenticationSuccess}
    />
  );
};

export const AmplifyAuthContent = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasIdToken, setHasIdToken] = useState<boolean>(false);
  const [idToken, setIdToken] = useState<string | undefined>(undefined);
  const { apiService } = useContext(ApiContext);

  const {
    data: whoAmIData,
    //loading: whoAmILoading,
    //error: whoAmIError,
  } = useWhoAmIQuery({
    client: apiService.getClient(),
    skip: !isAuthenticated,
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        setError(null); // Clear any previous errors
        console.log("Checking authentication...");
        const user = await getCurrentUser();
        console.log("User found:", user);
        if (user) {
          setIsAuthenticated(true);
          await getToken();
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        // getCurrentUser() throws UserUnAuthenticatedException when no user is logged in
        // This is expected behavior, not an error
        console.log(
          "User not authenticated (expected if no user logged in):",
          err
        );
        setIsAuthenticated(false);
        setError(null); // Don't treat this as an error
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const getToken = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        setIdToken(token);
        BgwAuthenticationService.setIdToken(token);
        PAuthenticationService.setIdToken(token);
        setHasIdToken(true);
        console.info("Tokens have been set");
      } else {
        console.warn("No ID token found in session");
        setHasIdToken(false);
      }
    } catch (e) {
      console.error("Error getting token:", e);
      setError(e as Error);
      setHasIdToken(false);
    }
  };

  // Set up token refresh callback
  useEffect(() => {
    const handleTokenRefresh = (newToken: string) => {
      console.info("Token refreshed in AmplifyAuthContent");
      setIdToken(newToken);
      BgwAuthenticationService.setIdToken(newToken);
      PAuthenticationService.setIdToken(newToken);
    };

    BgwAuthenticationService.setOnTokenRefresh(handleTokenRefresh);
    PAuthenticationService.setOnTokenRefresh(handleTokenRefresh);

    return () => {
      BgwAuthenticationService.setOnTokenRefresh(() => {});
      PAuthenticationService.setOnTokenRefresh(() => {});
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getToken();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && whoAmIData?.whoAmI?.email) {
      // heap.identify(whoAmIData.whoAmI.email);
    }
  }, [isAuthenticated, whoAmIData]);

  if (isLoading) {
    return <AuthenticationLoading />;
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Box sx={{ textAlign: "center", color: "#333333" }}>
          <Typography variant="h5" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body1">{error.message}</Typography>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<Unauthorized />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (!hasIdToken) {
    return <AuthenticationLoading />;
  }

  return <IdTokenProvider value={{ idToken }}>{children}</IdTokenProvider>;
};

export const AmplifyAuthProvider = ({ children }: { children: ReactNode }) => {
  return <AmplifyAuthContent>{children}</AmplifyAuthContent>;
};
